import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  QrCode,
  ShieldCheck,
  Clock,
  Warning,
  ArrowRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const cards = [
  { key: "total_generated", label: "Total QR Generated", icon: QrCode, accent: "text-[#253c80]", bg: "bg-[#253c80]/10" },
  { key: "total_verified", label: "Total Verified", icon: ShieldCheck, accent: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "total_unverified", label: "Total Unverified", icon: Clock, accent: "text-amber-600", bg: "bg-amber-50" },
  { key: "duplicate_scans", label: "Duplicate Scans", icon: Warning, accent: "text-[#DC4226]", bg: "bg-[#DC4226]/10" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([
        api.get("/admin/dashboard/stats"),
        api.get("/admin/dashboard/recent"),
      ]);
      setStats(s.data);
      setRecent(r.data);
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Overview</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Control Room
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            Real-time view of QR issuance, verification activity, and tamper signals across all dealers.
          </p>
        </div>
        <Link
          to="/generate"
          data-testid="dashboard-generate-cta"
          className="inline-flex items-center gap-2 rounded-md bg-[#DC4226] hover:bg-[#b3351e] text-white px-4 py-2.5 text-sm font-semibold transition-colors"
        >
          <QrCode size={16} weight="bold" /> Generate new batch
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map(({ key, label, icon: Icon, accent, bg }) => (
          <div
            key={key}
            data-testid={`stat-card-${key}`}
            className="rounded-lg border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div className={`h-10 w-10 rounded-md ${bg} grid place-items-center`}>
                <Icon size={20} weight="duotone" className={accent} />
              </div>
            </div>
            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
              <div className="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
                {stats ? (stats[key] ?? 0).toLocaleString() : "—"}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Recent scan activity</h3>
            <p className="text-xs text-slate-500">Latest 15 verification scans from the field</p>
          </div>
          <Link
            to="/reports"
            className="text-sm font-semibold text-[#253c80] hover:underline inline-flex items-center gap-1"
            data-testid="dashboard-view-reports"
          >
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Unique Code</th>
                <th className="text-left px-6 py-3 font-semibold">Part Number</th>
                <th className="text-left px-6 py-3 font-semibold">Part Name</th>
                <th className="text-left px-6 py-3 font-semibold">Scanned</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    No scans yet. Generate a batch and share the QR codes to start tracking.
                  </td>
                </tr>
              )}
              {recent.map((row, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-[12px] text-slate-700">{row.unique_code}</td>
                  <td className="px-6 py-3 text-slate-700">{row.part_number}</td>
                  <td className="px-6 py-3 text-slate-700">{row.part_name}</td>
                  <td className="px-6 py-3 text-slate-500 text-xs">{new Date(row.scanned_at).toLocaleString()}</td>
                  <td className="px-6 py-3">
                    {row.is_duplicate ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#DC4226]/10 text-[#DC4226] px-2.5 py-0.5 text-xs font-semibold">
                        Duplicate
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold">
                        First scan
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
